// src/components/pediatric/FinalReviewTab.tsx
import React, { useEffect, useState } from "react";
import "../../css/FinalReviewTab.css";
import api from "../../api/axiosConfig";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";

const FinalReviewTab: React.FC = () => {
  const { emrNumber } = useParams<{ emrNumber: string }>();
  const { doctor } = useAuth();

  const location = useLocation();
  const caseId = (location.state as any)?.caseId;
  const navigate = useNavigate();

  const [mallampatiClass, setMallampatiClass] = useState<string | null>(null);

  const [teleSections, setTeleSections] = useState<{
    lungs: boolean;
    heart: boolean;
    arteries: boolean;
    abdomen: boolean;
  } | null>(null);

  const [mapiData, setMapiData] = useState<any>(null);

  const [additionalNotes, setAdditionalNotes] = useState("");
  const [isReviewed, setIsReviewed] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        const [mallampatiRes, teleRes, mapiRes] = await Promise.all([
          api.dataCollectionApi.get(`/api/teledocs/mallampati/${emrNumber}`),
          api.dataCollectionApi.get(
            `/api/teledocs/tele-auscultation/${emrNumber}`,
          ),
          api.dataCollectionApi.get(`/api/teledocs/mapi/${emrNumber}`),
        ]);

        // ✅ Mallampati
        setMallampatiClass(
          mallampatiRes?.data?.data?.classification?.title || null,
        );

        // ✅ Tele
        const sections = teleRes?.data?.data?.sections;
        if (sections) {
          setTeleSections({
            lungs: sections.lungs?.hasConcerns || false,
            heart: sections.heart?.hasConcerns || false,
            arteries: sections.arteries?.hasConcerns || false,
            abdomen: sections.abdomen?.hasConcerns || false,
          });
        }

        // ✅ mAPI
        setMapiData(mapiRes?.data?.data || null);
      } catch (error) {
        console.error("Error fetching final review data", error);
      } finally {
        setLoading(false);
      }
    };

    if (emrNumber) fetchData();
  }, [emrNumber]);

  if (loading) {
    return <div className="loading-state">Loading...</div>;
  }

  const hasAnyConcern =
    teleSections &&
    (teleSections.lungs ||
      teleSections.heart ||
      teleSections.arteries ||
      teleSections.abdomen);

  const finalScore = mapiData?.protocol?.finalMapiScore;

  const qualifyingSelected = [];

  if (mapiData?.qualifying?.experiencedWheezing === true) {
    qualifyingSelected.push("Has experienced wheezing");
  }

  if (mapiData?.qualifying?.episodesLast12Months === true) {
    qualifyingSelected.push("Episodes in last 12 months");
  }

  const majorCriteriaSelected = Object.entries(mapiData?.majorCriteria || {})
    .filter(([_, value]) => value === true)
    .map(([key]) => {
      if (key === "historyAsthmaInParent") return "History of asthma in parent";
      if (key === "sensitivityToAeroallergens")
        return "Sensitivity to aeroallergens";
      if (key === "riskOfAtopicDermatitis") return "Atopic Dermatitis";
      return null;
    })
    .filter(Boolean);

  const minorCriteriaSelected = Object.entries(mapiData?.minorCriteria || {})
    .filter(([_, value]) => value === true || value === "selected")
    .map(([key]) => {
      if (key === "eosinophilsGte4Pct") return "Eosinophils ≥4%";
      if (key === "allergyToMilkEggPeanuts")
        return "Allergy to milk, egg, peanuts";
      if (key === "wheezingUnrelatedToCold")
        return "Wheezing unrelated to cold";
      return null;
    })
    .filter(Boolean);

  const handleFinalSubmit = async () => {
    if (!isReviewed) {
      alert("Please confirm review before submitting.");
      return;
    }

    if (!additionalNotes.trim()) {
      alert("Please fill the Additional Notes before submitting.");
      return;
    }

    try {
      const payload = {
        emrId: emrNumber,
        // doctorName: mapiData?.doctorName || "Dr. John Doe",
        doctorName:
          mapiData?.doctorName || (doctor ? `Dr. ${doctor.name}` : null),

        mallampati: {
          klass: mallampatiClass?.replace("Class ", "") as
            | "I"
            | "II"
            | "III"
            | "IV",
        },

        auscultation: {
          lungs: {
            status: teleSections?.lungs ? "concern" : "no_concern",
          },
          heart: {
            status: teleSections?.heart ? "concern" : "no_concern",
          },
          arteries: {
            status: teleSections?.arteries ? "concern" : "no_concern",
          },
          abdomen: {
            status: teleSections?.abdomen ? "concern" : "no_concern",
          },
        },

        mapi: {
          qualifying: [
            {
              name: "experiencedWheezing",
              value: mapiData?.qualifying?.experiencedWheezing || false,
            },
            {
              name: "episodesLast12Months",
              value: mapiData?.qualifying?.episodesLast12Months || false,
            },
          ],

          majorCriteria: Object.entries(mapiData?.majorCriteria || {})
            .filter(([_, value]) => value === true)
            .map(([key]) => {
              if (key === "historyAsthmaInParent")
                return "History of asthma in parent";
              if (key === "sensitivityToAeroallergens")
                return "Sensitivity to aeroallergens";
              if (key === "riskOfAtopicDermatitis") return "Atopic Dermatitis";
              return null;
            })
            .filter(Boolean),

          minorCriteria: Object.entries(mapiData?.minorCriteria || {})
            .filter(([_, value]) => value === true || value === "Yes")
            .map(([key]) => {
              if (key === "eosinophilsGte4Pct") return "Eosinophils ≥4%";
              if (key === "allergyToMilkEggPeanuts")
                return "Allergy to milk, egg, peanuts";
              if (key === "wheezingUnrelatedToCold")
                return "Wheezing unrelated to cold";
              return null;
            })
            .filter(Boolean),

          finalScore: finalScore === "Yes" ? "Positive" : "Negative",
        },

        notes: additionalNotes,
      };

      await api.dataCollectionApi.post("/api/teledocs/final-review", payload);

      // 🔥 Complete PEDIATRIC_RESIDENT session
      if (caseId) {
        try {
          await api.CenterworkflowApi.post(
            `/postworkflow/cases/${caseId}/tasks/PEDIATRIC_RESIDENT/complete`,
          );
        } catch (err) {
          console.error("Failed to complete session", err);
        }
      }

      alert("Final Review submitted successfully!");

      // 🔄 Navigate back to pediatric queue
      navigate("/queue/pediatric");
    } catch (error) {
      console.error("Final Review submission failed", error);
      alert("Failed to submit Final Review.");
    }
  };

  const handleReset = () => {
    setAdditionalNotes("");
  };

  return (
    <div className="final-review-container">
      <div className="assessment-summary-column">
        <h3 className="summary-main-title">Assessment Summary</h3>

        {/* Mallampati Classification Card */}
        <div className="summary-card">
          <div className="card-header">
            <h4>Mallampati Classification</h4>
            <span className="status-tag completed">&#10003; Completed</span>
          </div>
          <div className="card-body">
            <p>
              <strong>Class:</strong> {mallampatiClass || "--"}
            </p>
          </div>
        </div>

        {/* Tele-Auscultation Card */}
        <div className="summary-card">
          <div className="card-header">
            <h4>Tele-Auscultation</h4>
            <span
              className={`status-tag ${
                hasAnyConcern ? "concern" : "completed"
              }`}
            >
              {hasAnyConcern ? "ⓘ Concerns Found" : "✓ No Concerns"}
            </span>
          </div>
          <div className="card-body auscultation-summary">
            <div>
              <span
                className={`status-icon ${
                  teleSections?.lungs ? "yellow" : "green"
                }`}
              >
                {teleSections?.lungs ? "ⓘ" : "✔"}
              </span>{" "}
              <strong>Lungs:</strong>{" "}
              {teleSections?.lungs ? "Concern" : "No Concern"}
            </div>

            <div>
              <span
                className={`status-icon ${
                  teleSections?.heart ? "yellow" : "green"
                }`}
              >
                {teleSections?.heart ? "ⓘ" : "✔"}
              </span>{" "}
              <strong>Heart:</strong>{" "}
              {teleSections?.heart ? "Concern" : "No Concern"}
            </div>

            <div>
              <span
                className={`status-icon ${
                  teleSections?.arteries ? "yellow" : "green"
                }`}
              >
                {teleSections?.arteries ? "ⓘ" : "✔"}
              </span>{" "}
              <strong>Arteries:</strong>{" "}
              {teleSections?.arteries ? "Concern" : "No Concern"}
            </div>

            <div>
              <span
                className={`status-icon ${
                  teleSections?.abdomen ? "yellow" : "green"
                }`}
              >
                {teleSections?.abdomen ? "ⓘ" : "✔"}
              </span>{" "}
              <strong>Abdomen:</strong>{" "}
              {teleSections?.abdomen ? "Concern" : "No Concern"}
            </div>
          </div>
        </div>

        {/* Modified Asthma Predictive Index Card */}
        <div className="summary-card">
          <div className="card-header">
            <h4>Modified Asthma Predictive Index</h4>
            <span
              className={`status-tag ${
                finalScore === "Yes" ? "positive" : "completed"
              }`}
            >
              {finalScore === "Yes"
                ? "ⓘ Positive"
                : finalScore === "No"
                  ? "✓ Negative"
                  : "--"}
            </span>
          </div>
          <div className="card-body">
            <p>
              <strong>Qualifying Criteria:</strong>{" "}
              {qualifyingSelected.length > 0
                ? qualifyingSelected.join(", ")
                : "--"}
            </p>

            <p>
              <strong>Major Criteria:</strong>{" "}
              {majorCriteriaSelected.length > 0
                ? majorCriteriaSelected.join(", ")
                : "--"}
            </p>

            <p>
              <strong>Minor Criteria:</strong>{" "}
              {minorCriteriaSelected.length > 0
                ? minorCriteriaSelected.join(", ")
                : "--"}
            </p>

            <p>
              <strong>Final Score:</strong>{" "}
              <span
                className={finalScore === "Yes" ? "positive-text" : undefined}
              >
                {finalScore === "Yes"
                  ? "Positive"
                  : finalScore === "No"
                    ? "Negative"
                    : "--"}
              </span>
            </p>
          </div>
        </div>

        {/* Additional Notes */}
        <div className="additional-notes-section">
          <h4>Additional Notes</h4>
          <textarea
            placeholder="Enter any additional observations or notes for the final report..."
            maxLength={500}
            value={additionalNotes}
            onChange={(e) => setAdditionalNotes(e.target.value)}
          />
          <div className="char-counter">
            {additionalNotes.length}/500 characters
          </div>
        </div>
      </div>

      {/* <div className="action-panel-column">
        <button className="action-btn primary" onClick={handleFinalSubmit}>
          Submit
        </button>
        <button className="action-btn secondary" onClick={handleReset}>
          Reset
        </button>
      </div> */}

      <div className="action-panel-column">
        {doctor && (
          <label className="review-checkbox">
            <input
              type="checkbox"
              checked={isReviewed}
              onChange={(e) => setIsReviewed(e.target.checked)}
            />
            Reviewed by Dr. {doctor.name}
          </label>
        )}

        <button className="action-btn primary" onClick={handleFinalSubmit}>
          Submit
        </button>

        <button className="action-btn secondary" onClick={handleReset}>
          Reset
        </button>
      </div>

      <div className="footer-bar standalone">
        <div className="progress-container">
          <div className="progress-bar">
            <div className="progress-fill" style={{ width: "100%" }}></div>
          </div>
          <span>4 of 4 steps completed</span>
        </div>
      </div>
    </div>
  );
};

export default FinalReviewTab;
